import { ethers } from 'ethers';
// Use no-axios build: default `@stellar/stellar-sdk/contract` pulls axios-backed HTTP; with axios@1 +
// CRA/webpack, `http-client` `create` can be undefined (`(0, _httpClient.create) is not a function`).
import { Client } from '@stellar/stellar-sdk/no-axios/contract';

/** Match `@stellar/stellar-sdk/lib/bindings/utils.sanitizeIdentifier` (contract method keys on `Client`). */
function sanitizeIdentifier(identifier) {
    const reservedNames = new Set([
        'break', 'case', 'catch', 'class', 'const', 'continue', 'debugger', 'default', 'delete', 'do', 'else',
        'export', 'extends', 'finally', 'for', 'function', 'if', 'import', 'in', 'instanceof', 'new', 'return',
        'super', 'switch', 'this', 'throw', 'try', 'typeof', 'var', 'void', 'while', 'with', 'yield', 'enum',
        'implements', 'interface', 'let', 'package', 'private', 'protected', 'public', 'static', 'async', 'await',
        'constructor', 'null', 'true', 'false',
    ]);
    let sanitized = String(identifier).replace(/[^a-zA-Z0-9_$]/g, '_');
    if (reservedNames.has(sanitized)) sanitized = `${sanitized}_`;
    if (/^\d/.test(sanitized)) sanitized = `_${sanitized}`;
    if (sanitized === '' || /^_+$/.test(sanitized)) sanitized = '_unnamed';
    return sanitized;
}

function posArgsToNamed(spec, wasmMethodName, values) {
    const fn = spec.getFunc(wasmMethodName);
    const inputs = fn.inputs();
    if (inputs.length !== values.length) {
        throw new Error(`Soroban ${wasmMethodName}: expected ${inputs.length} arg(s), got ${values.length}`);
    }
    const out = {};
    for (let i = 0; i < inputs.length; i += 1) {
        out[inputs[i].name().toString()] = values[i];
    }
    return out;
}

function unwrapRustResult(v) {
    if (v && typeof v.isOk === 'function') {
        if (!v.isOk()) return null;
        return v.unwrap();
    }
    return v;
}

/**
 * Rust `Option<T>` sometimes surfaces from the JS client as `{ tag: 'Some'|'None', values: [...] }`
 * instead of plain `null` / inner value (depends on spec / SDK path).
 */
function unwrapSorobanOption(val) {
    if (val == null) return null;
    if (typeof val !== 'object' || Array.isArray(val)) return val;
    if (!Object.prototype.hasOwnProperty.call(val, 'tag')) return val;
    const tag = String(val.tag);
    if (/^none$/i.test(tag)) return null;
    if (/^some$/i.test(tag) && Array.isArray(val.values)) {
        if (val.values.length === 0) return null;
        return val.values.length === 1 ? val.values[0] : val.values;
    }
    return val;
}

/** Soroban / JSON may surface bools as 0/1 — avoid `Boolean("false")` pitfalls. */
function toScBool(v) {
    return v === true || v === 1 || v === 1n;
}

function toEthersBn(v) {
    if (v == null) return ethers.BigNumber.from(0);
    if (typeof v === 'bigint') return ethers.BigNumber.from(v.toString());
    if (v && v._isBigNumber) return v;
    return ethers.BigNumber.from(v.toString());
}

function bytesToHexCompat(v) {
    if (v == null) return '0x';
    if (typeof v === 'string' && v.startsWith('0x')) return v;
    if (typeof v === 'string') return ethers.utils.hexlify(new TextEncoder().encode(v));
    if (v instanceof Uint8Array) return ethers.utils.hexlify(v);
    try {
        return ethers.utils.hexlify(Uint8Array.from(v));
    } catch (_) {
        return '0x';
    }
}

function pickField(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const k of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined) return obj[k];
    }
    return undefined;
}

function emptyAccountCoreTuple() {
    return [
        false,
        false,
        false,
        ethers.BigNumber.from(0),
        '0x',
        ethers.BigNumber.from(0),
        ethers.BigNumber.from(0),
    ];
}

/** `get_account_core` returns `Option<AccountCore>` — None → empty tuple (no account). */
function accountCoreNativeToTuple(raw) {
    const u0 = unwrapRustResult(raw);
    const u = unwrapSorobanOption(u0);
    if (u == null) return emptyAccountCoreTuple();
    if (Array.isArray(u)) {
        return [
            toScBool(u[0]),
            toScBool(u[1]),
            toScBool(u[2]),
            toEthersBn(u[3]),
            bytesToHexCompat(u[4]),
            toEthersBn(u[5]),
            toEthersBn(u[6]),
        ];
    }
    return [
        toScBool(pickField(u, 'exists')),
        toScBool(pickField(u, 'finalized')),
        toScBool(pickField(u, 'pending_action', 'pendingAction')),
        toEthersBn(pickField(u, 'tx_id', 'txId')),
        bytesToHexCompat(pickField(u, 'elgamal_pubkey', 'elgamalPubkey')),
        toEthersBn(pickField(u, 'pending_credit_counter', 'pendingCreditCounter')),
        toEthersBn(pickField(u, 'minimum_pending_credit_counter', 'minimumPendingCreditCounter')),
    ];
}

function pendingKindToNumber(kind) {
    if (kind == null) return 0;
    if (typeof kind === 'number' || typeof kind === 'bigint') return Number(kind);
    if (typeof kind === 'string') return Number(kind) || 0;
    return 0;
}

function encodeVecBytesField(data) {
    if (data == null) return '0x';
    if (typeof data === 'string' && data.startsWith('0x')) return data;
    if (data instanceof Uint8Array) return bytesToHexCompat(data);
    if (Array.isArray(data)) {
        try {
            const parts = data.map((d) => {
                if (d instanceof Uint8Array) return d;
                if (typeof d === 'string' && d.startsWith('0x')) return ethers.utils.arrayify(d);
                return new Uint8Array(0);
            });
            return ethers.utils.hexlify(ethers.utils.concat(parts));
        } catch (_) {
            return '0x';
        }
    }
    return '0x';
}

/**
 * `PendingAction` from confidential-mirror `types.rs` → tuple matching EVM `getPendingAction`:
 * `(kind, owner, tx_id, token, recipient, amount, fee_paid, use_offchain_verify, data)`.
 */
function pendingActionNativeToTuple(raw) {
    const empty = () => [
        ethers.BigNumber.from(0),
        ethers.constants.AddressZero,
        ethers.BigNumber.from(0),
        ethers.constants.AddressZero,
        ethers.constants.AddressZero,
        ethers.BigNumber.from(0),
        ethers.BigNumber.from(0),
        false,
        '0x',
    ];
    const u = unwrapRustResult(raw);
    if (u == null) return empty();
    const addr = (v) => (v == null ? ethers.constants.AddressZero : String(v));
    if (Array.isArray(u)) {
        return [
            toEthersBn(u[0]),
            addr(u[1]),
            toEthersBn(u[2]),
            addr(u[3]),
            addr(u[4]),
            toEthersBn(u[5]),
            toEthersBn(u[6]),
            toScBool(u[7]),
            bytesToHexCompat(u[8]),
        ];
    }
    return [
        ethers.BigNumber.from(pendingKindToNumber(pickField(u, 'kind'))),
        addr(pickField(u, 'owner')),
        toEthersBn(pickField(u, 'tx_id', 'txId')),
        addr(pickField(u, 'token')),
        addr(pickField(u, 'recipient')),
        toEthersBn(pickField(u, 'amount')),
        toEthersBn(pickField(u, 'fee_paid', 'feePaid')),
        toScBool(pickField(u, 'use_offchain_verify', 'useOffchainVerify')),
        encodeVecBytesField(pickField(u, 'data')),
    ];
}

function resolveWasmMethod(spec, preferredNames) {
    const names = new Set(spec.funcs().map((f) => f.name().toString()));
    for (const n of preferredNames) {
        if (names.has(n)) return n;
    }
    return null;
}

function hexByteLen(hex) {
    if (hex == null || typeof hex !== 'string') return null;
    const h = hex.startsWith('0x') ? hex.slice(2) : hex;
    if (!h) return 0;
    return Math.floor(h.length / 2);
}

function hexPreview(hex, lead = 18, tail = 6) {
    if (hex == null || typeof hex !== 'string') return hex;
    const h = hex.startsWith('0x') ? hex : `0x${hex}`;
    if (h.length <= lead + tail + 1) return h;
    return `${h.slice(0, lead)}…${h.slice(-tail)}`;
}

/** Compact log object for `get_account_core` tuple (matches EVM `AccountCore`). */
function formatAccountCoreTupleForLog(t) {
    if (!t || !Array.isArray(t)) return { raw: t };
    return {
        exists: t[0],
        finalized: t[1],
        pending_action: t[2],
        tx_id: t[3]?.toString?.() ?? String(t[3]),
        elgamal_pubkey_bytes: hexByteLen(t[4]),
        elgamal_pubkey_hex: hexPreview(t[4]),
        pending_credit_counter: t[5]?.toString?.() ?? String(t[5]),
        minimum_pending_credit_counter: t[6]?.toString?.() ?? String(t[6]),
    };
}

/**
 * Soroban-backed object exposing the same read-method names as the ethers `Contract`
 * used by the app (`getAccountCore`, …), so UI code can stay chain-agnostic.
 *
 * @param {{ rpcUrl: string, networkPassphrase: string, contractId: string, publicKey?: string }} opts
 */
export async function createStellarGatewayContractFacade(opts) {
    const { rpcUrl, networkPassphrase, contractId, publicKey } = opts || {};
    if (!rpcUrl || !networkPassphrase || !contractId) {
        throw new Error('createStellarGatewayContractFacade: rpcUrl, networkPassphrase, and contractId are required');
    }

    const client = await Client.from({
        rpcUrl,
        contractId,
        networkPassphrase,
        publicKey: publicKey || undefined,
        allowHttp: rpcUrl.startsWith('http://'),
    });

    const spec = client.spec;
    const m = {
        getAccountCore: resolveWasmMethod(spec, ['get_account_core']),
        getPendingAction: resolveWasmMethod(spec, ['get_pending_action']),
        getFeeConfig: resolveWasmMethod(spec, ['get_fee_config']),
        getAvailable: resolveWasmMethod(spec, ['get_available']),
        getPending: resolveWasmMethod(spec, ['get_pending']),
    };

    async function invokeRead(wasmName, positionalArgs, mapResult) {
        if (!wasmName) {
            const err = new Error(`Soroban: method not found on contract spec (required entry missing)`);
            err.code = 'CALL_EXCEPTION';
            throw err;
        }
        const key = sanitizeIdentifier(wasmName);
        const fn = client[key];
        if (typeof fn !== 'function') {
            const err = new Error(`Soroban: client has no callable ${wasmName} (${key})`);
            err.code = 'CALL_EXCEPTION';
            throw err;
        }
        try {
            const argsObj = positionalArgs.length ? posArgsToNamed(spec, wasmName, positionalArgs) : undefined;
            const assembled = positionalArgs.length ? await fn(argsObj) : await fn();
            let r = assembled.result;
            if (r && typeof r.isErr === 'function' && r.isErr()) {
                const inner = r.unwrapErr?.() || r.error;
                const err = new Error(inner?.message || String(inner || 'Soroban contract returned Err'));
                err.code = 'CALL_EXCEPTION';
                throw err;
            }
            if (r && typeof r.isOk === 'function' && r.isOk()) {
                r = r.unwrap();
            }
            return mapResult(r);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[Soroban/confidential-mirror] read failed', {
                wasm: wasmName,
                args: positionalArgs,
                contractId,
                message: e?.message || String(e),
            });
            const err = new Error(e?.message || String(e));
            err.code = e?.code || 'CALL_EXCEPTION';
            throw err;
        }
    }

    const facade = {
        address: contractId,
        isStellarSoroban: true,
        signer: null,
        provider: null,
        getAccountCore: async (ownerAddr) => {
            // eslint-disable-next-line no-console
            console.log('[Soroban/confidential-mirror] get_account_core … invoking', {
                contractId,
                owner: ownerAddr,
            });
            const out = await invokeRead(m.getAccountCore, [ownerAddr], accountCoreNativeToTuple);
            // eslint-disable-next-line no-console
            console.log('[Soroban/confidential-mirror] get_account_core → tuple', {
                contractId,
                owner: ownerAddr,
                account: formatAccountCoreTupleForLog(out),
            });
            return out;
        },
        getPendingAction: async (ownerAddr) => {
            const out = await invokeRead(m.getPendingAction, [ownerAddr], pendingActionNativeToTuple);
            // eslint-disable-next-line no-console
            console.log('[Soroban/confidential-mirror] get_pending_action → tuple', {
                contractId,
                owner: ownerAddr,
                kind: out[0]?.toString?.() ?? out[0],
                tx_id: out[2]?.toString?.() ?? out[2],
                token: out[3],
                recipient: out[4],
                amount: out[5]?.toString?.() ?? out[5],
                fee_paid: out[6]?.toString?.() ?? out[6],
                use_offchain_verify: out[7],
                data_hex_bytes: hexByteLen(out[8]),
            });
            return out;
        },
        /** EVM-only helper — confidential-mirror has no Soroban equivalent; derive from `get_account_core`. */
        debugCanSubmitFlags: async (ownerAddr) => {
            const t = await invokeRead(m.getAccountCore, [ownerAddr], accountCoreNativeToTuple);
            return [toScBool(t[0]), toScBool(t[1]), toScBool(t[2]), false, false];
        },
        getAvailable: async (ownerAddr, tokenAddr) => {
            const raw = await invokeRead(m.getAvailable, [ownerAddr, tokenAddr], (r) =>
                unwrapSorobanOption(unwrapRustResult(r))
            );
            let c1 = '0x';
            let c2 = '0x';
            if (raw == null) {
                c1 = '0x';
                c2 = '0x';
            } else if (Array.isArray(raw)) {
                c1 = bytesToHexCompat(raw[0]);
                c2 = bytesToHexCompat(raw[1]);
            } else {
                c1 = bytesToHexCompat(pickField(raw, 'c1', 'avail_c1', 'available_c1'));
                c2 = bytesToHexCompat(pickField(raw, 'c2', 'avail_c2', 'available_c2'));
            }
            const availRawShape =
                raw == null
                    ? 'none'
                    : Array.isArray(raw)
                        ? `array(len=${raw.length})`
                        : `object(keys=${Object.keys(raw).join(',')})`;
            // eslint-disable-next-line no-console
            console.log('[Soroban/confidential-mirror] get_available', {
                contractId,
                owner: ownerAddr,
                token: tokenAddr,
                option_some: raw != null,
                raw_shape: availRawShape,
                c1_bytes: hexByteLen(c1),
                c2_bytes: hexByteLen(c2),
                c1_hex: hexPreview(c1),
                c2_hex: hexPreview(c2),
            });
            return [c1, c2];
        },
        getPending: async (ownerAddr, tokenAddr) => {
            const raw = await invokeRead(m.getPending, [ownerAddr, tokenAddr], (r) =>
                unwrapSorobanOption(unwrapRustResult(r))
            );
            let c1 = '0x';
            let c2 = '0x';
            if (raw == null) {
                c1 = '0x';
                c2 = '0x';
            } else if (Array.isArray(raw)) {
                c1 = bytesToHexCompat(raw[0]);
                c2 = bytesToHexCompat(raw[1]);
            } else {
                c1 = bytesToHexCompat(pickField(raw, 'c1', 'pending_c1'));
                c2 = bytesToHexCompat(pickField(raw, 'c2', 'pending_c2'));
            }
            const rawShape =
                raw == null
                    ? 'none'
                    : Array.isArray(raw)
                        ? `array(len=${raw.length})`
                        : `object(keys=${Object.keys(raw).join(',')})`;
            // eslint-disable-next-line no-console
            console.log('[Soroban/confidential-mirror] get_pending', {
                contractId,
                owner: ownerAddr,
                token: tokenAddr,
                option_some: raw != null,
                raw_shape: rawShape,
                c1_bytes: hexByteLen(c1),
                c2_bytes: hexByteLen(c2),
                c1_hex: hexPreview(c1),
                c2_hex: hexPreview(c2),
            });
            return [c1, c2];
        },
        /** Mirrors EVM `feeToken()` via `get_fee_config` → `(fee_account, fee_amount, fee_token)`. */
        feeToken: async () => {
            const raw = await invokeRead(m.getFeeConfig, [], (r) => unwrapRustResult(r));
            if (raw == null) return ethers.constants.AddressZero;
            if (Array.isArray(raw) && raw.length >= 3) return String(raw[2]);
            return ethers.constants.AddressZero;
        },
        /** Mirrors EVM `feeAmount()` via `get_fee_config`. */
        feeAmount: async () => {
            const raw = await invokeRead(m.getFeeConfig, [], (r) => unwrapRustResult(r));
            if (raw == null) return ethers.BigNumber.from(0);
            if (Array.isArray(raw) && raw.length >= 2) return toEthersBn(raw[1]);
            return ethers.BigNumber.from(0);
        },
    };

    return facade;
}
